import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTES } from '@traction/shared';

export function ExtensionAuthPage() {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'connecting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!loading && !user) {
      // Redirect to login with return URL
      const returnUrl = encodeURIComponent(ROUTES.EXTENSION_AUTH);
      navigate(`${ROUTES.LOGIN}?returnUrl=${returnUrl}`);
    }
  }, [loading, user, navigate]);

  async function handleConnect() {
    if (!user) return;

    setConnectionStatus('connecting');

    try {
      // Get the Firebase ID token
      const idToken = await user.getIdToken();

      // Create auth payload for extension
      const authPayload = {
        type: 'TRACTION_AUTH_TOKEN',
        token: idToken,
        userId: user.uid,
        email: user.email,
        displayName: profile?.full_name || user.displayName || '',
        expiresIn: 3600000, // 1 hour
      };

      // Dispatch custom event that extension content script will listen for
      window.dispatchEvent(new CustomEvent('traction-extension-auth', {
        detail: authPayload,
      }));

      // Also try postMessage for cross-origin communication
      window.postMessage(authPayload, '*');

      setConnectionStatus('success');
    } catch (error) {
      setConnectionStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to connect');
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cacao-dark">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-cacao-dark-tertiary border-t-cacao-gold" />
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect to login
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-cacao-dark p-4">
      <div className="w-full max-w-md rounded-2xl bg-cacao-dark-secondary border border-cacao-dark-tertiary p-8 shadow-lg">
        <div className="flex items-center justify-center gap-3">
          <span className="text-4xl">🐝</span>
          <h1 className="text-2xl font-bold text-[#F5F5F5]">Traction Bee</h1>
        </div>

        <h2 className="mt-6 text-center text-xl font-semibold text-[#F5F5F5]">
          Connect Chrome Extension
        </h2>

        <p className="mt-2 text-center text-cacao-muted">
          Link your Traction Bee account to the Gmail extension
        </p>

        <div className="mt-8 rounded-lg bg-cacao-dark-tertiary/50 p-4">
          <p className="text-sm text-cacao-muted">Signed in as</p>
          <p className="font-medium text-[#F5F5F5]">{profile?.full_name || user.displayName || 'User'}</p>
          <p className="text-sm text-cacao-muted">{user.email}</p>
        </div>

        {connectionStatus === 'idle' && (
          <button
            onClick={handleConnect}
            className="mt-6 w-full rounded-lg bg-cacao-gold px-4 py-3 font-medium text-cacao-dark hover:bg-cacao-gold/90 transition-colors"
          >
            Connect Extension
          </button>
        )}

        {connectionStatus === 'connecting' && (
          <div className="mt-6 flex items-center justify-center gap-2 rounded-lg bg-cacao-dark-tertiary px-4 py-3">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-cacao-dark-tertiary border-t-cacao-gold" />
            <span className="text-cacao-muted">Connecting...</span>
          </div>
        )}

        {connectionStatus === 'success' && (
          <div className="mt-6">
            <div className="flex items-center justify-center gap-2 rounded-lg bg-green-500/10 border border-green-500/30 px-4 py-3 text-green-400">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">Connected successfully!</span>
            </div>
            <p className="mt-4 text-center text-sm text-[#F5F5F5]">
              You can now close this tab and open Gmail to see your leads.
            </p>
            <button
              onClick={() => window.close()}
              className="mt-4 w-full rounded-lg bg-cacao-gold px-4 py-3 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
            >
              Close this tab
            </button>
            <a
              href="https://mail.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 block w-full text-center rounded-lg border border-cacao-gold/30 px-4 py-3 text-sm font-medium text-cacao-gold hover:bg-cacao-gold/10 transition-colors"
            >
              Open Gmail
            </a>
          </div>
        )}

        {connectionStatus === 'error' && (
          <div className="mt-6">
            <div className="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-red-400">
              <p className="font-medium">Connection failed</p>
              <p className="text-sm">{errorMessage}</p>
            </div>
            <button
              onClick={handleConnect}
              className="mt-4 w-full rounded-lg bg-cacao-gold px-4 py-3 font-medium text-cacao-dark hover:bg-cacao-gold/90 transition-colors"
            >
              Try again
            </button>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-cacao-muted">
          Make sure the Traction Bee extension is installed before connecting.
        </p>
      </div>
    </div>
  );
}
